import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMolecule } from '@peruselab/api-client/molecules';
import { computeMolecularFormula } from '@peruselab/structure-editor/chemistry/moleculeFormula';

interface MoleculeFormulaLabelProps {
  readonly moleculeId: string;
}

type FormulaState =
  | { readonly status: 'loading' }
  | { readonly status: 'ready'; readonly formula: string }
  | { readonly status: 'error'; readonly message: string };

// #139: a read-only rendering of a bare Molecule Illustration element -- a Molecule has no
// depiction of its own, so per the schema's own stated intent this renders as its computed
// molecular formula (e.g. "C3H7NO2") rather than a drawn structure. No pointer/selection/undo
// interaction, purely a display component -- same spirit as IsomerThumbnail.
export function MoleculeFormulaLabel({ moleculeId }: MoleculeFormulaLabelProps) {
  const { t } = useTranslation();
  const [state, setState] = useState<FormulaState>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    (async () => {
      const molecule = await getMolecule(moleculeId);
      const formula = await computeMolecularFormula(molecule.document);
      if (!cancelled) setState({ status: 'ready', formula });
    })().catch((error: unknown) => {
      if (!cancelled) {
        setState({
          status: 'error',
          message: error instanceof Error ? error.message : t('moleculeFormulaLabel.computeError')
        });
      }
    });
    return () => {
      cancelled = true;
    };
  }, [moleculeId, t]);

  return (
    <div className="molecule-formula-label">
      {state.status === 'loading' ? <span className="isomer-thumbnail-status">{t('common.loading')}</span> : null}
      {state.status === 'error' ? (
        <span className="isomer-thumbnail-status isomer-thumbnail-error">{state.message}</span>
      ) : null}
      {state.status === 'ready' ? <span className="molecule-formula-text">{state.formula}</span> : null}
    </div>
  );
}
